/**
 * ErrorBoundary — catches render-time exceptions in its subtree so a single
 * crashing page or chart can't blank the whole app. Reports to Sentry and
 * renders a recoverable fallback (ErrorState) with a "Retry" that clears the
 * error and re-mounts the subtree.
 *
 * Must be a class component — `componentDidCatch` / `getDerivedStateFromError`
 * have no hook equivalent. The fallback itself is the functional <ErrorState>,
 * which uses `useT()`; that's fine because every boundary we mount sits inside
 * <AppProvider> (see main.tsx), so the i18n context is available.
 *
 * Place one around the routed page area (so a page crash keeps the shell/nav
 * alive) and around volatile widgets (ChatWidget, ECharts panels) that fail
 * independently of the page they're on. Pass `resetKeys` (e.g. the route path)
 * to auto-clear the error when the user navigates away.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { ErrorState } from './ui/ErrorState';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback. Receives a `reset` to clear the error. Defaults to <ErrorState>. */
  fallback?: (reset: () => void) => ReactNode;
  /** When any value here changes between renders, a captured error is cleared. */
  resetKeys?: unknown[];
  /** Render the compact ErrorState variant (for inline/chart placement). */
  compact?: boolean;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  componentDidUpdate(prev: ErrorBoundaryProps) {
    // Auto-recover when the caller's resetKeys change (e.g. route navigation).
    if (this.state.error && prev.resetKeys && this.props.resetKeys) {
      const changed =
        prev.resetKeys.length !== this.props.resetKeys.length ||
        prev.resetKeys.some((k, i) => !Object.is(k, this.props.resetKeys![i]));
      if (changed) this.reset();
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (this.state.error) {
      if (this.props.fallback) return this.props.fallback(this.reset);
      return <ErrorState error={this.state.error} onRetry={this.reset} compact={this.props.compact} />;
    }
    return this.props.children;
  }
}
